import { useMemo } from "react";
import { DeliveryTag } from "./delivery-tag";
import { SaleInfoTag } from "./sale-info-tag";
import * as Styled from "./style";
import { isVideoSeason, isVideoLegacySeason } from "~/components/blocks/product/type-guard";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";

type DmmInfoTagProps = {
  videoData: G_GetVideoQuery["video"];
};

/**
 * DMMでの取扱情報を表示するタグコンポーネント
 * - 舞台とスポット配信の場合、一律でDMMでの取り扱い情報タグは表示しないこと
 * @see https://confl.arms.dmm.com/pages/viewpage.action?pageId=1230359116
 */
export const DmmInfoTag = (props: DmmInfoTagProps) => {
  const { videoData } = props;

  const { isExclusive, isNewArrival, customTag, campaign, viewingTypes, seasonType } = videoData;

  const { content } = useQueryString();

  /** 作品の次回エピソード配信日の`startDeliveryAt` */
  const nextEpisodeStartDeliveryAt = useMemo(() => {
    if (isVideoSeason(videoData)) {
      const { nextDeliveryEpisode } = videoData;
      return nextDeliveryEpisode.startDeliveryAt;
    }

    return;
  }, [videoData]);

  /** 配信前フラグ */
  const isNextEpisodeBeforeDelivered = useMemo(() => {
    if (isVideoSeason(videoData)) {
      const { nextDeliveryEpisode } = videoData;
      return nextDeliveryEpisode.isBeforeDelivered;
    }

    return false;
  }, [videoData]);

  /** 配信前タグフラグ */
  const isPublicBeingDelivered = useMemo(() => {
    if (isVideoSeason(videoData)) {
      const { isBeingDelivered, isPublic } = videoData;
      return !isBeingDelivered && isPublic;
    }
    return false;
  }, [videoData]);

  /** 1つでも販売中が存在する場合は`true`になる。すべてが販売終了した場合は`false`になる */
  const isOnSale = useMemo(() => {
    if (isVideoSeason(videoData)) {
      if (content !== undefined) {
        // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
        if (!videoData.episode) return;

        const {
          episode: { ppvProducts },
        } = videoData;

        return ppvProducts.some((cur) => cur.isOnSale);
      } else {
        const {
          episodes: { edges },
        } = videoData;

        if (edges.length === 0) return;
        if (edges[0].node.ppvProducts.length === 0) return;
        return edges[0].node.ppvProducts.some((cur) => cur.isOnSale);
      }
    }

    if (isVideoLegacySeason(videoData)) {
      const {
        content: { ppvProducts },
      } = videoData;

      return ppvProducts.some((cur) => cur.isOnSale);
    }

    return;
  }, [videoData]);

  return (
    <Styled.DivFlexWrap>
      <DeliveryTag
        isExclusive={isExclusive}
        isNewArrival={isNewArrival}
        customTag={customTag}
        isOnSale={isOnSale}
        nextEpisodeStartDeliveryAt={nextEpisodeStartDeliveryAt}
        isNextEpisodeBeforeDelivered={isNextEpisodeBeforeDelivered}
        isPublicBeingDelivered={isPublicBeingDelivered}
        viewingTypes={viewingTypes}
        seasonType={seasonType}
      />

      <SaleInfoTag campaign={campaign} />
    </Styled.DivFlexWrap>
  );
};
